/* eslint-disable global-require, import/first, import/no-import-module-exports */

// Polyfills
import 'blueimp-canvas-to-blob';

import './sentry';
import './preload-error';

// Each moment locale import switches the global locale, so nl must stay last.
import 'moment/locale/th';
import 'moment/locale/nl';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import * as R from 'ramda';
import cssVars from 'css-vars-ponyfill';
import * as qs from 'qs';
import Storage from '../shared/common/services/storage';
import Socket from '../shared/common/services/socket';
import memoize from '../shared/common/utils/memoize';
import * as i18n from '../shared/i18n';
import configureStore from '../shared/configure-store';
import * as ui from '../shared/modules/core/reducers/ui';
import { getInitialPromotionsState } from '../shared/modules/core/reducers/promotions';
import { resolveTrialLanguageLocale } from '../shared/modules/authentication/containers/free-trial/trial-locale.ts';

// Set driver for storage service
Storage.setDriver(window.localStorage);

const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
if (query.refresh_token) Storage.setCookie('refresh_token', query.refresh_token, [10, 'years']);

// Setup locale
const storedLocale = Storage.get('fa_locale');
const browserLocale = (navigator.language || navigator.userLanguage || '').split('-');

// First paint: marketing query only; FreeTrialContainer switches UI when domain/API suggest nl.
const trialLocale = resolveTrialLanguageLocale(query);
const onFreeTrial = window.location.pathname === '/free-trial'
  || window.location.pathname.startsWith('/free-trial/');
i18n.setup(onFreeTrial ? trialLocale : (storedLocale || browserLocale[0]));

const App = require('./app').default;

require('../shared/common/styles/app.scss');

const hasNativeCssVarsSupport = window.CSS && window.CSS.supports && window.CSS.supports('(--a: 0)');

cssVars({
  onlyVars: true,
  watch: true,
  updateDOM: hasNativeCssVarsSupport,
  include: 'link[rel="stylesheet"][href^="/static"]',
  onComplete: (styles) => {
    const node = document.getElementById('css-var-polyfill') || document.createElement('style');

    node.id = 'css-var-polyfill'; // eslint-disable-line no-param-reassign
    node.appendChild(document.createTextNode(styles));

    document.body.appendChild(node);
  },
});

// Lazy-load the base styles chunk for faster initial styling (background
// color, font etc.). Note: the old `!process.env.NODE_ENV !== 'development'`
// guard here was always true, so this branch ran in every environment; the
// dynamic import preserves that behaviour and works in webpack and Vite.
import('../shared/common/styles/base.scss');

const { userAgent } = window.navigator;
const IE = userAgent.indexOf('MSIE ') > -1 || userAgent.indexOf('Trident/') > -1;

if (IE) {
  import('../shared/common/styles/ie.scss');
}

const network = Storage.getObject('currentNetwork');
const organisation = Storage.getObject('currentOrganisation');
const localState = Storage.getObject('localState');

// Create array from segments in url
const url = R.pipe(R.reject(R.equals('/')), R.drop(1))(window.location.pathname.split('/'));
const networkId = network ? network.id : null;

// If renderer from server set preloaded state, else read from localStorage
const preloadedState = window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.organisation.selected
  ? window.__PRELOADED_STATE__
  : {
    network: { selected: url[0] === 'networks' ? url[1] : networkId },
    organisation: { selected: organisation || null },
    networks: { items: network ? { [network.id]: network } : {} },
    loggedUser: { isLoggedIn: !!Storage.getCookie('refresh_token') },
    dismissed: {
      survey_release: !!Storage.getCookie('dismiss_survey_release'),
    },
  };

// Garbage collect preloaded state
delete window.__PRELOADED_STATE__;

// Create store
const store = configureStore({
  ...preloadedState,
  ui: ui.mapLocalStorageToState(localState),
  promotions: getInitialPromotionsState(),
});

// Initialise services/utils
memoize.setStore(store);
Socket.connect(store);

ReactDOM.render(
  <BrowserRouter>
    <App store={store} />
  </BrowserRouter>,
  document.getElementById('root'),
);
